import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filterStatus) params.append('status', filterStatus);
      if (search) params.append('search', search);

      const res = await fetch(`${API}/api/orders?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
    setLoading(false);
  }, [page, filterStatus, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/orders/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({ status })
      });
      fetchOrders();
    } catch (err) {
      console.error('Failed to update order:', err);
    }
  };

  const handleDownload = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.append('status', filterStatus);
    window.open(`${API}/api/orders/download/csv?${params}`, '_blank');
  };

  const statusColors = {
    pending: { bg: '#FEF3C7', color: '#D97706' },
    confirmed: { bg: '#DBEAFE', color: '#2563EB' },
    installed: { bg: '#D1FAE5', color: '#059669' },
    cancelled: { bg: '#FEE2E2', color: '#DC2626' },
  };

  const statCounts = {
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    installed: orders.filter(o => o.status === 'installed').length,
  };

  return (
    <div style={{ padding: '32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Home Charger Orders</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{total} order{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={handleDownload} style={btnStyle}>
          <i className="fas fa-download" style={{ marginRight: 8 }}></i>Download CSV
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Pending', count: statCounts.pending, icon: 'fa-clock', color: '#D97706', bg: '#FFFBEB' },
          { label: 'Confirmed', count: statCounts.confirmed, icon: 'fa-check-circle', color: '#2563EB', bg: '#EFF6FF' },
          { label: 'Installed', count: statCounts.installed, icon: 'fa-plug', color: '#059669', bg: '#ECFDF5' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 14, padding: '20px 24px', border: `1px solid ${s.color}22` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className={`fas ${s.icon}`} style={{ color: s.color, fontSize: 18 }}></i>
              <span style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</span>
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search name, email, postcode..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={inputStyle}
        />
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="installed">Installed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Orders list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', background: 'white', borderRadius: 16, border: '1px solid #E5E7EB' }}>No orders found</div>
        ) : (
          orders.map(order => {
            const sc = statusColors[order.status] || statusColors.pending;
            const expanded = expandedId === order.id;
            return (
              <div key={order.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                {/* Summary row */}
                <div
                  onClick={() => setExpandedId(expanded ? null : order.id)}
                  style={{ padding: '16px 20px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1fr 1fr 100px 120px 40px', alignItems: 'center', gap: 12 }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{order.full_name}</div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>{order.email}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 14 }}>{order.postcode}</div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>{order.charger_type}</div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    {order.total_amount ? `£${parseFloat(order.total_amount).toFixed(2)}` : '—'}
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, textAlign: 'center', textTransform: 'capitalize' }}>
                    {order.status}
                  </span>
                  <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ color: '#9CA3AF' }}></i>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div style={{ padding: '0 20px 20px', borderTop: '1px solid #F3F4F6' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                      <DetailRow label="Phone" value={order.phone} />
                      <DetailRow label="Address" value={`${order.address_line1 || ''}${order.address_line2 ? ', ' + order.address_line2 : ''}`} />
                      <DetailRow label="City" value={order.city} />
                      <DetailRow label="Postcode" value={order.postcode} />
                      <DetailRow label="Charger Type" value={order.charger_type} />
                      <DetailRow label="Adaptor" value={order.add_adaptor ? 'Yes (+£59.99)' : 'No'} />
                      <DetailRow label="Cable" value={order.add_cable ? 'Yes (+£189.99)' : 'No'} />
                      <DetailRow label="Installation Location" value={order.installation_location} />
                      <DetailRow label="App Interest" value={order.app_interest ? 'Yes' : 'No'} />
                      <DetailRow label="Date" value={new Date(order.created_at).toLocaleString('en-GB')} />
                    </div>
                    
                    {/* Status actions */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', padding: '8px 0', marginRight: 8 }}>Update status:</span>
                      {['pending', 'confirmed', 'installed', 'cancelled'].map(s => (
                        <button
                          key={s}
                          onClick={() => updateStatus(order.id, s)}
                          disabled={order.status === s}
                          style={{
                            padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB', cursor: 'pointer',
                            fontSize: 13, fontWeight: 500, textTransform: 'capitalize',
                            background: order.status === s ? (statusColors[s]?.bg || '#F3F4F6') : 'white',
                            color: order.status === s ? (statusColors[s]?.color || '#374151') : '#374151',
                            opacity: order.status === s ? 0.7 : 1,
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>← Prev</button>
          <span style={{ padding: '8px 16px', fontSize: 14, color: '#6B7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Next →</button>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 14, color: '#111827', marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}

const btnStyle = { display: 'inline-flex', alignItems: 'center', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 };
const inputStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, minWidth: 240, background: '#F9FAFB' };
const selectStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, background: '#F9FAFB', cursor: 'pointer' };
const pageBtnStyle = { padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 };
